import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/api';

// ── Tier pricing (admin; Phase 1 slice C) ──
//
// The marquee accounts (Avendra hotels, "10% off illy") buy at fixed per-item
// tier prices. In SOS those can only be edited by hand and never follow a list
// increase; here they're portal-owned, with list price, effective discount,
// anomaly flags, and an audit trail next to every row.
//
// While PRICING_FROM_PORTAL is off, edits here are staged: customers are still
// charged the SOS copy. The banner says which mode is live. "Compare with SOS"
// shows every price where an order typed straight into SOS would differ from
// an order placed through the portal.

const RED = '#A50B18';
const money = v => (v == null || isNaN(v) ? '—' : `$${Number(v).toFixed(2)}`);
const FLAG_LABEL = { free: 'free ($0)', 'above-list': 'above list', 'not-in-catalog': 'not in catalog' };

export default function PricingTab({ readOnly }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tierId, setTierId] = useState(null);
  const [edits, setEdits] = useState({});
  const [rowStatus, setRowStatus] = useState({});
  const [filter, setFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [history, setHistory] = useState({});
  const [catalog, setCatalog] = useState(null);
  const [addSearch, setAddSearch] = useState('');
  const [addPrice, setAddPrice] = useState({});
  const [livePreview, setLivePreview] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleMode, setRuleMode] = useState('fixed');
  const [rulePct, setRulePct] = useState('10');
  const [rulePreview, setRulePreview] = useState(null);
  const [ruleBusy, setRuleBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await adminApi.getPricing();
      setData(d);
      setTierId(prev => prev || d.tiers[0]?.tierId || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function runImport() {
    setImporting(true);
    setError('');
    try {
      await adminApi.importPricingFromSos();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setImporting(false);
    }
  }

  async function loadDiff() {
    setDiffLoading(true);
    try {
      setDiff(await adminApi.getPricingSosDiff());
    } catch (e) {
      setError(e.message);
    } finally {
      setDiffLoading(false);
    }
  }

  async function save(row) {
    const key = `${row.tierId}#${row.itemId}`;
    const value = edits[key];
    if (value == null || value === '') return;
    const reason = window.prompt(`Reason for changing ${row.itemName} (optional — shows in the history):`, '') ?? null;
    if (reason === null) return;
    setRowStatus(s => ({ ...s, [key]: 'saving' }));
    try {
      await adminApi.setTierPrice(row.tierId, row.itemId, value, reason);
      setEdits(e => { const n = { ...e }; delete n[key]; return n; });
      setRowStatus(s => ({ ...s, [key]: 'saved' }));
      setHistory(h => { const n = { ...h }; delete n[key]; return n; });
      await load();
    } catch (e) {
      setRowStatus(s => ({ ...s, [key]: e.message || 'failed' }));
    }
  }

  async function remove(row) {
    const reason = window.prompt(`Remove ${row.itemName} from this tier? Customers on the tier will pay list price for it.\n\nReason (optional):`, '');
    if (reason === null) return;
    try {
      await adminApi.removeTierPrice(row.tierId, row.itemId, reason);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function toggleHistory(row) {
    const key = `${row.tierId}#${row.itemId}`;
    if (history[key]) { setHistory(h => { const n = { ...h }; delete n[key]; return n; }); return; }
    try {
      const r = await adminApi.getTierPriceHistory(row.tierId, row.itemId);
      setHistory(h => ({ ...h, [key]: r.history || [] }));
    } catch (e) {
      setError(e.message);
    }
  }

  async function previewLive(next) {
    setLiveBusy(true);
    setError('');
    try {
      setLivePreview(await adminApi.setPricingLive(next, { dryRun: true }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLiveBusy(false);
    }
  }

  async function confirmLive() {
    const next = livePreview.live;
    const reason = window.prompt(
      (next ? 'Turn ON portal pricing? Customers on a tier will pay the prices in this screen.'
            : 'Turn OFF portal pricing? Customers go back to the prices copied from SOS.') +
      `\n\n${livePreview.changes.length} customer price${livePreview.changes.length === 1 ? '' : 's'} will change. Takes effect within a minute.` +
      '\n\nReason (shows in the history):', '');
    if (reason === null) return;
    setLiveBusy(true);
    try {
      await adminApi.setPricingLive(next, { reason });
      setLivePreview(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setLiveBusy(false);
    }
  }

  function openRulePanel(tier) {
    setRuleMode(tier.mode === 'percent' ? 'percent' : 'fixed');
    setRulePct(tier.percentOff != null ? String(tier.percentOff) : '10');
    setRulePreview(null);
    setRuleOpen(true);
  }

  async function previewRule() {
    setRuleBusy(true);
    setError('');
    try {
      setRulePreview(await adminApi.setTierMode(tierId, { mode: ruleMode, percentOff: ruleMode === 'percent' ? rulePct : null, dryRun: true }));
    } catch (e) {
      setError(e.message);
    } finally {
      setRuleBusy(false);
    }
  }

  async function applyRule() {
    const n = rulePreview?.changes?.length || 0;
    const reason = window.prompt(
      `Apply this pricing rule? ${n} price${n === 1 ? '' : 's'} will change` +
      (live ? ' for customers immediately.' : ' (customers are unaffected until portal pricing is switched on).') +
      '\n\nReason (shows in the history):', '');
    if (reason === null) return;
    setRuleBusy(true);
    try {
      await adminApi.setTierMode(tierId, { mode: ruleMode, percentOff: ruleMode === 'percent' ? rulePct : null, reason });
      setRuleOpen(false);
      setRulePreview(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setRuleBusy(false);
    }
  }

  async function unpin(row) {
    try {
      await adminApi.unpinTierPrice(row.tierId, row.itemId);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function addOnRule(product) {
    try {
      await adminApi.addTierItemOnRule(tierId, product.id);
      setAddSearch('');
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function ensureCatalog() {
    if (catalog) return;
    try {
      const c = await adminApi.getCatalog();
      setCatalog(c.products || []);
    } catch (e) {
      setError(e.message);
    }
  }

  async function addItem(product) {
    const price = addPrice[product.id];
    if (price == null || price === '') return;
    try {
      await adminApi.setTierPrice(tierId, product.id, price, 'Added to tier');
      setAddPrice(p => { const n = { ...p }; delete n[product.id]; return n; });
      setAddSearch('');
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  if (loading && !data) return <p style={st.muted}>Loading tier pricing…</p>;

  const tiers = data?.tiers || [];
  const live = data?.portalPricingLive;

  if (!tiers.length) {
    return (
      <div>
        {error && <p style={st.error}>{error}</p>}
        <div style={st.card}>
          <h3 style={{ marginTop: 0 }}>Tier pricing isn't set up in the portal yet</h3>
          <p style={st.muted}>
            Importing copies the current tier price lists from SOS exactly as they are. Nothing customers pay
            changes — the portal keeps charging the same prices until portal pricing is switched on.
          </p>
          {!readOnly && (
            <button style={st.btnPrimary} disabled={importing} onClick={runImport}>
              {importing ? 'Importing…' : 'Import tier prices from SOS'}
            </button>
          )}
        </div>
      </div>
    );
  }

  const rows = (data.rows || []).filter(r => r.tierId === tierId);
  const currentTier = tiers.find(t => t.tierId === tierId);
  const ruleTier = currentTier?.mode === 'percent';
  const q = filter.trim().toLowerCase();
  const shown = q ? rows.filter(r => `${r.itemName} ${r.sku || ''}`.toLowerCase().includes(q)) : rows;
  const flagged = rows.filter(r => r.flags.length);
  const onTier = new Set(rows.map(r => String(r.itemId)));
  const addMatches = (catalog && addSearch.trim().length >= 2)
    ? catalog.filter(p => !onTier.has(String(p.id))
        && `${p.name} ${p.sku}`.toLowerCase().includes(addSearch.trim().toLowerCase())).slice(0, 8)
    : [];

  return (
    <div>
      <div style={{ ...st.banner, ...(live ? st.bannerLive : {}), display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <span>
          {live
            ? <><b>Portal pricing is ON.</b> Customers on a tier pay the prices in this screen. Orders typed directly into SOS still use SOS's own list — use "Compare with SOS" to spot differences.</>
            : <><b>Portal pricing is OFF.</b> Customers pay the prices copied from SOS, exactly as before. Edits here are saved and take effect when portal pricing is turned on.</>}
          {data?.liveChangedAt && <span style={st.meta}>{' '}Last switched {new Date(data.liveChangedAt).toLocaleString()}{data.liveChangedBy ? ` by ${data.liveChangedBy.split('@')[0]}` : ''}.</span>}
        </span>
        {!readOnly && !livePreview && (
          <button style={live ? st.btnOutline : st.btnPrimary} disabled={liveBusy} onClick={() => previewLive(!live)}>
            {liveBusy ? 'Checking…' : live ? 'Turn off portal pricing…' : 'Turn on portal pricing…'}
          </button>
        )}
      </div>

      {livePreview && (
        <div style={{ ...st.card, marginBottom: '1rem', borderColor: '#A50B18' }}>
          <b>{livePreview.live ? 'Turning portal pricing ON' : 'Turning portal pricing OFF'} would change {livePreview.changes.length} customer price{livePreview.changes.length === 1 ? '' : 's'}</b>
          {livePreview.changes.length > 0 && <> ({livePreview.up} up, {livePreview.down} down)</>}. Nothing has changed yet.
          {livePreview.changes.length > 0 && (
            <table style={{ ...st.table, marginTop: '0.5rem' }}>
              <tbody>
                {livePreview.changes.map(c => (
                  <tr key={`${c.tierId}#${c.itemId}`}>
                    <td style={st.td}>{c.itemName || `Item ${c.itemId}`}</td>
                    <td style={st.td}><span style={st.meta}>{c.tierName}</span></td>
                    <td style={st.tdR}>{money(c.from)} → <b style={{ color: c.to > c.from ? '#2e7d32' : '#c0392b' }}>{money(c.to)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button style={st.btnPrimary} disabled={liveBusy} onClick={confirmLive}>
              {livePreview.live ? 'Turn on' : 'Turn off'}
            </button>
            <button style={st.linkBtn} onClick={() => setLivePreview(null)}>cancel</button>
          </div>
        </div>
      )}

      {error && <p style={st.error}>{error}</p>}

      <div style={st.toolbar}>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {tiers.map(t => (
            <button key={t.tierId}
              style={t.tierId === tierId ? { ...st.tierBtn, ...st.tierBtnOn } : st.tierBtn}
              onClick={() => setTierId(t.tierId)}>
              {t.name} <span style={{ opacity: 0.7 }}>({t.itemCount})</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input style={{ ...st.input, width: 220, marginBottom: 0 }} placeholder="Filter items…" value={filter} onChange={e => setFilter(e.target.value)} />
          <button style={st.btnOutline} disabled={diffLoading} onClick={loadDiff}>
            {diffLoading ? 'Comparing…' : 'Compare with SOS'}
          </button>
          {!readOnly && <button style={st.btnOutline} disabled={importing} onClick={runImport} title="Adds tier prices that exist in SOS but not here. Never overwrites a portal price.">
            {importing ? 'Importing…' : 'Import new from SOS'}
          </button>}
        </div>
      </div>

      {currentTier && (
        <div style={st.ruleBar}>
          <span>
            <b>{currentTier.name}</b> uses{' '}
            {currentTier.mode === 'percent'
              ? <b>{currentTier.percentOff}% off list</b>
              : <b>fixed prices</b>}
            {currentTier.mode === 'percent'
              ? ' — prices follow list automatically; a typed-in price becomes an exception.'
              : ' — each item keeps the price entered here until someone changes it.'}
          </span>
          {!readOnly && !ruleOpen && <button style={st.linkBtn} onClick={() => openRulePanel(currentTier)}>change pricing rule</button>}
        </div>
      )}

      {ruleOpen && (
        <div style={st.card}>
          <b style={{ fontSize: '0.9rem' }}>How should {currentTier?.name} be priced?</b>
          <div style={{ display: 'flex', gap: '1.25rem', margin: '0.6rem 0', flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={st.radio}><input type="radio" checked={ruleMode === 'fixed'} onChange={() => { setRuleMode('fixed'); setRulePreview(null); }} /> Fixed price per item</label>
            <label style={st.radio}>
              <input type="radio" checked={ruleMode === 'percent'} onChange={() => { setRuleMode('percent'); setRulePreview(null); }} />
              <input style={{ ...st.priceInput, width: 56 }} type="number" min="0.1" max="99" step="0.1" value={rulePct}
                disabled={ruleMode !== 'percent'} onChange={e => { setRulePct(e.target.value); setRulePreview(null); }} />
              % off list — follows list price automatically
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={st.btnOutline} disabled={ruleBusy} onClick={previewRule}>{ruleBusy && !rulePreview ? 'Checking…' : 'Preview changes'}</button>
            {rulePreview && <button style={st.btnPrimary} disabled={ruleBusy} onClick={applyRule}>Apply</button>}
            <button style={st.linkBtn} onClick={() => { setRuleOpen(false); setRulePreview(null); }}>cancel</button>
          </div>
          {rulePreview && (
            <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
              {rulePreview.changes.length === 0
                ? <span style={{ color: '#2e7d32' }}>No prices change. ✓</span>
                : <>
                    <b>{rulePreview.changes.length} price{rulePreview.changes.length === 1 ? '' : 's'} would change</b>
                    {' '}({rulePreview.up} up, {rulePreview.down} down, {rulePreview.unchanged} unchanged). Nothing is saved until you press Apply.
                    <table style={{ ...st.table, marginTop: '0.4rem' }}>
                      <tbody>
                        {rulePreview.changes.map(c => (
                          <tr key={c.itemId}>
                            <td style={st.td}>{c.itemName || `Item ${c.itemId}`}</td>
                            <td style={st.tdR}>list {money(c.listPrice)}</td>
                            <td style={st.tdR}>{money(c.from)} → <b style={{ color: c.to > c.from ? '#2e7d32' : '#c0392b' }}>{money(c.to)}</b></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>}
            </div>
          )}
        </div>
      )}

      {flagged.length > 0 && (
        <div style={st.flagBox}>
          <b>{flagged.length} price{flagged.length === 1 ? '' : 's'} on this tier look wrong:</b>{' '}
          {flagged.map(r => `${r.itemName} (${r.flags.map(f => FLAG_LABEL[f] || f).join(', ')})`).join(' · ')}
        </div>
      )}

      {diff && (
        <div style={st.diffBox}>
          {diff.inSync
            ? <span style={{ color: '#2e7d32' }}>Portal and SOS agree on every tier price. ✓</span>
            : <>
                <b>Portal and SOS disagree on {diff.differ.length + diff.onlyInPortal.length + diff.onlyInSos.length} price{(diff.differ.length + diff.onlyInPortal.length + diff.onlyInSos.length) === 1 ? '' : 's'}.</b>
                {' '}An order typed directly into SOS would charge the SOS price.
                <ul style={{ margin: '0.4rem 0 0', fontSize: '0.85rem' }}>
                  {diff.differ.map(d => (
                    <li key={`d${d.tierId}#${d.itemId}`}>{d.itemName || `Item ${d.itemId}`} (tier {d.tierId}): portal {money(d.portalPrice)} vs SOS {money(d.sosPrice)}</li>
                  ))}
                  {diff.onlyInPortal.map(d => <li key={`p${d.tierId}#${d.itemId}`}>{d.itemName || `Item ${d.itemId}`} (tier {d.tierId}): only in portal ({money(d.price)}) — SOS charges list</li>)}
                  {diff.onlyInSos.map(d => <li key={`s${d.tierId}#${d.itemId}`}>{d.itemName || `Item ${d.itemId}`} (tier {d.tierId}): only in SOS ({money(d.price)}) — portal charges list</li>)}
                </ul>
              </>}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={st.table}>
          <thead>
            <tr>
              <th style={st.th}>Item</th>
              <th style={st.thR}>List</th>
              <th style={st.thR}>Tier price</th>
              <th style={st.thR}>Discount</th>
              <th style={st.th}>Last change</th>
              <th style={st.th}></th>
            </tr>
          </thead>
          <tbody>
            {shown.map(r => {
              const key = `${r.tierId}#${r.itemId}`;
              const editing = edits[key] != null;
              const status = rowStatus[key];
              return [
                <tr key={key} style={r.flags.length ? { background: '#fff8f0' } : undefined}>
                  <td style={st.td}>
                    <div style={{ fontWeight: 600 }}>{r.itemName}</div>
                    <div style={st.meta}>
                      {r.sku || `SOS item ${r.itemId}`}
                      {r.flags.map(f => <span key={f} style={st.flag}>{FLAG_LABEL[f] || f}</span>)}
                      {ruleTier && (r.followsRule
                        ? <span style={st.ruleBadge}>follows {currentTier.percentOff}% rule</span>
                        : <span style={st.pinBadge}>exception{!readOnly && <> · <button style={st.linkBtn} onClick={() => unpin(r)}>back on rule</button></>}</span>)}
                    </div>
                  </td>
                  <td style={st.tdR}>{money(r.listPrice)}</td>
                  <td style={st.tdR}>
                    {readOnly ? money(r.price) : (
                      <span style={{ display: 'inline-flex', gap: '0.3rem', alignItems: 'center' }}>
                        <input style={st.priceInput} type="number" step="0.01" min="0"
                          value={editing ? edits[key] : Number(r.price).toFixed(2)}
                          onChange={e => setEdits(x => ({ ...x, [key]: e.target.value }))} />
                        {editing && <button style={st.saveBtn} onClick={() => save(r)}>Save</button>}
                      </span>
                    )}
                    {status && status !== 'saving' && status !== 'saved' && <div style={st.error}>{status}</div>}
                  </td>
                  <td style={st.tdR}>{r.discountPct == null ? '—' : `${r.discountPct}%`}</td>
                  <td style={st.td}>
                    <span style={st.meta}>
                      {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '—'}
                      {r.updatedBy ? ` · ${r.updatedBy.split('@')[0]}` : ''}
                      {r.source === 'sos-import' ? ' · from SOS' : ''}
                    </span>
                    {' '}<button style={st.linkBtn} onClick={() => toggleHistory(r)}>history</button>
                  </td>
                  <td style={st.td}>
                    {!readOnly && <button style={st.removeBtn} title="Remove from tier" onClick={() => remove(r)}>×</button>}
                  </td>
                </tr>,
                history[key] && (
                  <tr key={`${key}-h`}>
                    <td colSpan={6} style={{ ...st.td, background: '#fafafa' }}>
                      {history[key].length === 0 ? <span style={st.meta}>No history.</span> : (
                        <ul style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>
                          {history[key].map(h => (
                            <li key={h.at}>
                              {new Date(h.at).toLocaleString()} — {h.action}: {money(h.oldPrice)} → {money(h.newPrice)} by {h.by}{h.reason ? ` (${h.reason})` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div style={{ ...st.card, marginTop: '1rem' }}>
          <b style={{ fontSize: '0.9rem' }}>Add an item to {tiers.find(t => t.tierId === tierId)?.name}</b>
          <input style={{ ...st.input, marginTop: '0.5rem' }} placeholder="Search catalog by name or SKU…"
            value={addSearch} onFocus={ensureCatalog} onChange={e => setAddSearch(e.target.value)} />
          {addMatches.map(p => (
            <div key={p.id} style={st.addRow}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                <div style={st.meta}>{p.sku} · list {money(p.price)}</div>
              </div>
              <input style={st.priceInput} type="number" step="0.01" min="0" placeholder="Tier price"
                value={addPrice[p.id] ?? ''} onChange={e => setAddPrice(x => ({ ...x, [p.id]: e.target.value }))} />
              <button style={st.saveBtn} onClick={() => addItem(p)}>{ruleTier ? 'Add as exception' : 'Add'}</button>
              {ruleTier && <button style={st.saveBtn} onClick={() => addOnRule(p)}>Add on {currentTier.percentOff}% rule</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const st = {
  muted: { color: '#888', fontSize: '0.9rem' },
  error: { color: '#c0392b', fontSize: '0.85rem' },
  card: { background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '1rem' },
  banner: { background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.7rem 1rem', fontSize: '0.88rem', marginBottom: '1rem', color: '#444' },
  bannerLive: { background: '#eef7ee', border: '1px solid #cde8cd', color: '#1b5e20' },
  toolbar: { display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' },
  tierBtn: { padding: '0.45rem 0.9rem', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.88rem' },
  tierBtnOn: { background: RED, border: `1px solid ${RED}`, color: '#fff', fontWeight: 600 },
  input: { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box', marginBottom: '0.5rem' },
  btnPrimary: { background: RED, color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.1rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { background: '#fff', color: RED, border: `1px solid ${RED}`, borderRadius: 6, padding: '0.45rem 0.8rem', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' },
  flagBox: { background: '#fff8f0', border: '1px solid #ffd8b0', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.85rem', marginBottom: '0.75rem', color: '#8a4b00' },
  diffBox: { background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.88rem', marginBottom: '0.75rem' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff' },
  th: { textAlign: 'left', fontSize: '0.75rem', color: '#888', fontWeight: 600, padding: '0.5rem', borderBottom: '2px solid #eee', textTransform: 'uppercase' },
  thR: { textAlign: 'right', fontSize: '0.75rem', color: '#888', fontWeight: 600, padding: '0.5rem', borderBottom: '2px solid #eee', textTransform: 'uppercase' },
  td: { padding: '0.5rem', borderBottom: '1px solid #f0f0f0', fontSize: '0.88rem', verticalAlign: 'top' },
  tdR: { padding: '0.5rem', borderBottom: '1px solid #f0f0f0', fontSize: '0.88rem', textAlign: 'right', verticalAlign: 'top', whiteSpace: 'nowrap' },
  meta: { fontSize: '0.75rem', color: '#888' },
  flag: { marginLeft: '0.4rem', background: '#ffe0b2', color: '#8a4b00', borderRadius: 8, padding: '0 0.4rem', fontSize: '0.7rem' },
  priceInput: { width: 90, padding: '0.3rem 0.4rem', border: '1px solid #ddd', borderRadius: 5, textAlign: 'right', fontSize: '0.88rem' },
  saveBtn: { background: RED, color: '#fff', border: 'none', borderRadius: 5, padding: '0.3rem 0.6rem', fontSize: '0.8rem', cursor: 'pointer' },
  removeBtn: { background: 'none', border: 'none', color: '#c0392b', fontSize: '1.1rem', cursor: 'pointer' },
  linkBtn: { background: 'none', border: 'none', color: RED, fontSize: '0.75rem', cursor: 'pointer', padding: 0, textDecoration: 'underline' },
  ruleBar: { display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', fontSize: '0.85rem', color: '#444', background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: '0.55rem 0.9rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  radio: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', cursor: 'pointer' },
  ruleBadge: { marginLeft: '0.4rem', background: '#e8f5e9', color: '#2e7d32', borderRadius: 8, padding: '0 0.4rem', fontSize: '0.7rem' },
  pinBadge: { marginLeft: '0.4rem', background: '#e3f2fd', color: '#1565c0', borderRadius: 8, padding: '0 0.4rem', fontSize: '0.7rem' },
  addRow: { display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid #f3f3f3' },
};
